import React from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import SectionHeading from '../components/ui/SectionHeading';
import Reveal from '../components/ui/Reveal';
import { aboutStory, milestones, pillars, services, stats } from '../data/content';
import { editorial, people, showroom } from '../data/collections';
import { site } from '../data/site';
import usePageMeta from '../hooks/usePageMeta';

export default function About() {
  usePageMeta({
    title: 'About | Pramukh Jewellers — Antique & Bridal Jewellers Since 1997',
    description:
      'Pramukh Jewellers has served families in Ahmedabad since 1997 — antique, bridal, temple, polki and diamond jewellery, hallmarked and handmade.',
    path: '/about',
  });

  return (
    <>
      <PageHeader
        eyebrow="Since 1997"
        title="Our story"
        lede="Nearly three decades of buying right, making it properly, and quoting the same honest number to everybody who walks in."
        image={showroom.counter}
        crumbs={[{ label: 'Home', to: '/' }, { label: 'About' }]}
      />

      {/* Story */}
      <section className="bg-cream py-24 lg:py-36">
        <div className="shell grid gap-16 lg:grid-cols-[1fr_0.85fr] lg:gap-24">
          <div>
            <SectionHeading
              index="01"
              eyebrow="The firm"
              title={'Three decades\nbehind one counter'}
            />

            <div className="mt-10 space-y-5">
              {aboutStory.map((p, i) => (
                <Reveal key={i} delay={i * 70}>
                  <p className="text-[15px] font-light leading-[1.85] text-royal/75">{p}</p>
                </Reveal>
              ))}
            </div>

            <Reveal delay={200} className="mt-14 grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label} className="border-t border-royal/15 pt-4">
                  <p className="font-display text-[2.1rem] leading-none text-royal">
                    {s.value}
                    {s.suffix && <span className="text-base text-gold">{s.suffix}</span>}
                  </p>
                  <p className="mt-2.5 text-[11.5px] uppercase tracking-wider2 text-royal/80">{s.label}</p>
                </div>
              ))}
            </Reveal>
          </div>

          {/* Order is fixed: founder, Kush Patel, then the work.
              Photos and captions come from `people` in data/collections.js. */}
          <Reveal delay={100} className="space-y-5">
            <figure className="wipe relative aspect-[4/5] overflow-hidden">
              <img
                src={people[0].img}
                alt={people[0].name}
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-royal-900/90 to-transparent p-5 pt-12">
                <p className="font-display text-[1.35rem] leading-tight text-cream">{people[0].name}</p>
                <p className="mt-1 text-[11.5px] uppercase tracking-wider2 text-gold-300">{people[0].role}</p>
              </figcaption>
            </figure>

            <div className="grid grid-cols-2 gap-5">
              {people.slice(1).map((p) => (
                <figure key={p.key} className="wipe relative aspect-square overflow-hidden">
                  <img src={p.img} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-royal-900/90 to-transparent p-4 pt-10">
                    <p className="font-display text-[1.05rem] leading-tight text-cream">{p.name}</p>
                    <p className="mt-0.5 text-[10.5px] uppercase tracking-wider2 text-gold-300">{p.role}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative overflow-hidden bg-royal py-24 lg:py-36">
        <div className="pointer-events-none absolute inset-0 paisley-bg opacity-[0.12]" />

        <div className="shell relative">
          <SectionHeading
            index="02"
            tone="light"
            eyebrow="Milestones"
            title="How we got here"
            lede="No dramatic pivots. Just a widening book and a bigger floor."
          />

          <div className="mt-16 grid gap-x-10 gap-y-10 md:grid-cols-2 lg:grid-cols-4">
            {milestones.map((m, i) => (
              <Reveal key={m.year} delay={i * 90} className="border-t border-cream/15 pt-5">
                <p className="font-display text-[2rem] leading-none text-gold-grad">{m.year}</p>
                <h3 className="mt-5 font-display text-[1.3rem] leading-snug text-cream">{m.title}</h3>
                <p className="mt-3 text-[14px] font-light leading-[1.75] text-cream/80">{m.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-t border-royal/10 bg-ivory py-24 lg:py-36">
        <div className="shell">
          <SectionHeading index="03" eyebrow="How we work" title="Four rules we do not bend" />

          <div className="mt-16 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 90} className="border-t border-royal/15 pt-5">
                <span className="text-[12px] tracking-wider2 text-gold-600">0{i + 1}</span>
                <h3 className="mt-4 font-display text-[1.35rem] leading-snug text-royal">{p.title}</h3>
                <p className="mt-3 text-[14px] font-light leading-[1.75] text-royal/70">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-royal/10 bg-cream py-24 lg:py-36">
        <div className="shell">
          <SectionHeading
            index="04"
            eyebrow="Services"
            title="What we can do for you"
            lede="Beyond selling from the case."
          />

          <div className="mt-16 grid gap-x-14 md:grid-cols-2">
            {services.map((s, i) => (
              <Reveal
                key={s.title}
                delay={(i % 2) * 70}
                className="grid grid-cols-[auto_1fr] gap-x-8 border-t border-royal/10 py-6"
              >
                <span className="pt-1 text-[12px] tracking-wider2 text-gold-600">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="font-display text-[1.35rem] leading-snug text-royal">{s.title}</h3>
                  <p className="mt-2 text-[14px] font-light leading-[1.75] text-royal/70">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-royal-900">
        <div className="absolute inset-0">
          <img src={editorial.shahiAbhushan} alt="" loading="lazy" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-royal-900 via-royal-900/85 to-royal-900/60" />
        </div>

        <div className="shell relative py-24 lg:py-36">
          <SectionHeading
            index="05"
            tone="light"
            eyebrow="Visit"
            title="Come and see the weight"
            lede={`Photographs only go so far. ${site.address.line2}, ${site.address.city} — we are open six days a week.`}
          />
          <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4">
            <Link to="/contact" className="btn-line-light">
              <span className="btn-line-rule" />
              Plan a visit
            </Link>
            <Link
              to="/collections"
              className="text-[12px] font-medium uppercase tracking-luxe text-cream/70 transition-colors duration-500 hover:text-gold-200"
            >
              Browse collections
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
